// ========================================
// CONFIGURAÇÃO DE STATUS DE CLIENTES
// src/config/clienteStatusConfig.js
// ========================================

import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  UserCheck, 
  UserX, 
  UserMinus,
  AlertTriangle,
  Ban
} from 'lucide-react';

// ========================================
// DEFINIÇÃO DOS STATUS DE CLIENTE
// ========================================
export const CLIENTE_STATUS = {
  // Status que PODEM fazer pedidos
  ATIVO: 'ATIVO',                   // Comprou nos últimos 4 meses
  INATIVO: 'INATIVO',               // Sem compra nos últimos 4 meses
  CADASTRADO: 'CADASTRADO',         // Cadastrado mas nunca comprou
  APROVADO: 'APROVADO',             // Recém aprovado (7 dias), depois vira CADASTRADO
  
  // Status que NÃO PODEM fazer pedidos
  EM_ANALISE: 'EM_ANALISE',         // Aguardando aprovação do cadastro
  REJEITADO: 'REJEITADO',           // Cadastro rejeitado
  EXCLUIDO: 'EXCLUIDO',             // Excluído pela Diretoria/Administrativo
};

// Mapeamento de status antigos para novos
export const MAPEAMENTO_STATUS_ANTIGO = {
  'PENDENTE': CLIENTE_STATUS.EM_ANALISE,
  'APROVADO': CLIENTE_STATUS.APROVADO,
  'REJEITADO': CLIENTE_STATUS.REJEITADO,
};

// ========================================
// CONFIGURAÇÃO VISUAL DOS STATUS
// ========================================
export const CLIENTE_STATUS_CONFIG = {
  [CLIENTE_STATUS.ATIVO]: {
    label: 'Cliente Ativo',
    descricao: 'Cliente com compras nos últimos 4 meses',
    cor: 'emerald',
    bgColor: 'bg-emerald-100',
    textColor: 'text-emerald-700',
    borderColor: 'border-emerald-300',
    icon: CheckCircle,
    podeFazerPedido: true,
  },
  [CLIENTE_STATUS.INATIVO]: {
    label: 'Cliente Inativo',
    descricao: 'Sem compras nos últimos 4 meses',
    cor: 'amber',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-300',
    icon: Clock,
    podeFazerPedido: true,
  },
  [CLIENTE_STATUS.CADASTRADO]: {
    label: 'Cliente Cadastrado',
    descricao: 'Cadastrado mas sem compras',
    cor: 'blue',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-300',
    icon: UserCheck,
    podeFazerPedido: true,
  },
  [CLIENTE_STATUS.APROVADO]: {
    label: 'Cadastro Aprovado',
    descricao: 'Aprovado recentemente (até 7 dias)',
    cor: 'green',
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    borderColor: 'border-green-300',
    icon: CheckCircle,
    podeFazerPedido: true,
  },
  [CLIENTE_STATUS.EM_ANALISE]: {
    label: 'Cadastro em Análise',
    descricao: 'Aguardando aprovação do cadastro',
    cor: 'amber',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-600',
    borderColor: 'border-amber-200',
    icon: Clock,
    podeFazerPedido: false,
    mensagemBloqueio: 'Este cliente está com o cadastro em análise. Aguarde a aprovação para criar pedidos.',
  },
  [CLIENTE_STATUS.REJEITADO]: {
    label: 'Cadastro Rejeitado',
    descricao: 'Cadastro foi rejeitado',
    cor: 'red',
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    borderColor: 'border-red-300',
    icon: XCircle,
    podeFazerPedido: false,
    mensagemBloqueio: 'Este cliente teve o cadastro rejeitado. Entre em contato com o Administrativo para mais detalhes.',
  },
  [CLIENTE_STATUS.EXCLUIDO]: {
    label: 'Cliente Excluído',
    descricao: 'Cliente excluído do sistema',
    cor: 'slate',
    bgColor: 'bg-slate-200',
    textColor: 'text-slate-700',
    borderColor: 'border-slate-400',
    icon: Ban,
    podeFazerPedido: false,
    mensagemBloqueio: 'Este cliente foi excluído do sistema. Entre em contato com o Administrativo para mais detalhes.',
  },
};

// ========================================
// FUNÇÕES AUXILIARES
// ========================================

/**
 * Verifica se um cliente pode fazer pedidos baseado no status
 */
export const clientePodeFazerPedido = (status) => {
  // Compatibilidade com status antigo PENDENTE
  const statusNormalizado = status === 'PENDENTE' ? CLIENTE_STATUS.EM_ANALISE : status;
  const config = CLIENTE_STATUS_CONFIG[statusNormalizado];
  return config?.podeFazerPedido ?? false;
};

/**
 * Retorna a mensagem de bloqueio para um status
 */
export const getMensagemBloqueio = (status) => {
  const statusNormalizado = status === 'PENDENTE' ? CLIENTE_STATUS.EM_ANALISE : status;
  const config = CLIENTE_STATUS_CONFIG[statusNormalizado];
  return config?.mensagemBloqueio || 'Cliente não pode fazer pedidos no momento.';
};

/**
 * Retorna a configuração completa de um status
 */
export const getClienteStatusConfig = (status) => {
  // Compatibilidade com status antigo PENDENTE
  const statusNormalizado = status === 'PENDENTE' ? CLIENTE_STATUS.EM_ANALISE : status;
  return CLIENTE_STATUS_CONFIG[statusNormalizado] || CLIENTE_STATUS_CONFIG[CLIENTE_STATUS.CADASTRADO];
};

/**
 * Calcula o status do cliente baseado nas regras de negócio
 * - ATIVO: comprou nos últimos 4 meses
 * - INATIVO: sem compra nos últimos 4 meses
 * - CADASTRADO: nunca comprou
 * - APROVADO -> CADASTRADO após 7 dias
 */
export const calcularStatusCliente = (cliente, ultimaCompra = null) => {
  // Se tem status de bloqueio, mantém
  if ([CLIENTE_STATUS.EM_ANALISE, CLIENTE_STATUS.REJEITADO, CLIENTE_STATUS.EXCLUIDO, 'PENDENTE'].includes(cliente.status_cadastro)) {
    return cliente.status_cadastro === 'PENDENTE' ? CLIENTE_STATUS.EM_ANALISE : cliente.status_cadastro;
  }

  // Se está APROVADO, verificar se já passou 7 dias
  if (cliente.status_cadastro === CLIENTE_STATUS.APROVADO && cliente.data_aprovacao) {
    const dataAprovacao = new Date(cliente.data_aprovacao);
    const hoje = new Date();
    const diasDesdeAprovacao = Math.floor((hoje - dataAprovacao) / (1000 * 60 * 60 * 24));
    
    if (diasDesdeAprovacao > 7) {
      // Já passou 7 dias, deve ser CADASTRADO ou calcular baseado em compras
      if (!ultimaCompra) {
        return CLIENTE_STATUS.CADASTRADO;
      }
    } else {
      // Ainda nos 7 dias de aprovação
      return CLIENTE_STATUS.APROVADO;
    }
  }

  // Se não tem compras, é CADASTRADO
  if (!ultimaCompra) {
    return CLIENTE_STATUS.CADASTRADO;
  }

  // Calcular baseado na última compra
  const dataUltimaCompra = new Date(ultimaCompra);
  const hoje = new Date();
  const mesesSemCompra = Math.floor((hoje - dataUltimaCompra) / (1000 * 60 * 60 * 24 * 30));

  if (mesesSemCompra <= 4) {
    return CLIENTE_STATUS.ATIVO;
  } else {
    return CLIENTE_STATUS.INATIVO;
  }
};

/**
 * Lista de status que aparecem na tela de aprovações
 */
export const STATUS_PARA_APROVACAO = [CLIENTE_STATUS.EM_ANALISE, 'PENDENTE'];

/**
 * Lista de status que podem ser alterados para EXCLUIDO
 */
export const STATUS_PODE_EXCLUIR = [
  CLIENTE_STATUS.ATIVO,
  CLIENTE_STATUS.INATIVO,
  CLIENTE_STATUS.CADASTRADO,
  CLIENTE_STATUS.APROVADO,
  CLIENTE_STATUS.REJEITADO,
];

export default {
  CLIENTE_STATUS,
  CLIENTE_STATUS_CONFIG,
  clientePodeFazerPedido,
  getMensagemBloqueio,
  getClienteStatusConfig,
  calcularStatusCliente,
  STATUS_PARA_APROVACAO,
  STATUS_PODE_EXCLUIR,
};
